import defaultAvatar from '@assets/default-avatar.png';
import { rateWorker } from '@company/services/rating/api';
import { useCompanyContext } from '@company/state/companyContext';
import { JobAssignmentRatingType } from '@company/types/job-assignment-rating.type';
import { StarRatingButton, ThLoading } from '@components/elements';
import ShowImage from '@components/elements/ShowImage';
import { zodResolver } from '@hookform/resolvers/zod';
import useAlert from '@hooks/useAlert';
import {
  IRatingVisibility,
  ReviewWorkerSchemaType,
  reviewWorkerValidationSchema,
} from '@th-types/rating.type';
import QueryKeys from '@constants/queryKeys';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useState } from 'react';
import { Alert, Badge, Button, Form, Stack } from 'react-bootstrap';
import { isMobile } from 'react-device-detect';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';

const COMMENTS_MAX_LENGTH = 300;

interface ComHubReviewWorkerFormProps {
  ratingAssignmentData: JobAssignmentRatingType;
  onSuccess: () => void;
  isReadOnly?: boolean;
}

function ComHubReviewWorkerForm({
  isReadOnly = false,
  ratingAssignmentData,
  onSuccess,
}: ComHubReviewWorkerFormProps) {
  const { premium: isPremiumCompany } = useCompanyContext();
  const queryClient = useQueryClient();
  const {
    workerRating,
    worker,
    jobTitle,
    totalWorkedDays,
    jobAssignmentId,
    jobId,
  } = ratingAssignmentData;

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    register,
    setValue,
  } = useForm<ReviewWorkerSchemaType>({
    resolver: zodResolver(reviewWorkerValidationSchema),
    mode: 'onChange',
    defaultValues: {
      jobAssignmentId,
      workerId: worker?.id,
      performance: workerRating?.performance,
      photoIsRepresentative: workerRating?.photoIsRepresentative ?? undefined,
      title: {
        id: jobTitle?.id,
        name: jobTitle?.name,
      },
      approximatelyDaysWorked: totalWorkedDays || 0,
      visibility: workerRating?.visibility || IRatingVisibility.PRIVATE,
      canceledUnexcused: workerRating?.canceledUnexcused || false,
      noCallNoShowed: workerRating?.noCallNoShowed || false,
      comments: workerRating?.comments || '',
      improvementComments: workerRating?.improvementComments || '',
      internalNotes: workerRating?.internalNotes || '',
    },
  });
  const { showWarningAlert, alertMessage } = useAlert();

  const [commentsCount, setCommentsCount] = useState<number>(0);
  const [internalNotesCount, setInternalNotesCount] = useState<number>(0);
  const [improvementCommentsCount, setImprovementCommentsCount] =
    useState<number>(0);

  // Mutation for rating worker with optimistic updates
  const { mutate: submitRating, isPending } = useMutation({
    mutationFn: rateWorker,
    onMutate: async (submittedData: ReviewWorkerSchemaType) => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({
        queryKey: [QueryKeys.WORKER_ASSIGNMENT_JOB, jobAssignmentId],
      });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData<JobAssignmentRatingType>([
        QueryKeys.WORKER_ASSIGNMENT_JOB,
        jobAssignmentId,
      ]);

      // Optimistically update the cache with submitted data
      queryClient.setQueryData<JobAssignmentRatingType>(
        [QueryKeys.WORKER_ASSIGNMENT_JOB, jobAssignmentId],
        (old) => {
          if (!old) return old;
          return {
            ...old,
            workerRatingAdded: true, // Mark as rated
            workerRating: {
              id: 0, // Temporary ID
              dateCreated: new Date().toISOString(),
              lastUpdated: new Date().toISOString(),
              verified: false,
              comments: submittedData.comments || null,
              performance: submittedData.performance,
              avgRating: null,
              submittedBy: 0,
              visibility: submittedData.visibility,
              ratingVersion: null,
              improvementComments: submittedData.improvementComments || null,
              internalNotes: submittedData.internalNotes,
              noCallNoShowed: submittedData.noCallNoShowed,
              canceledUnexcused: submittedData.canceledUnexcused,
              photoIsRepresentative: submittedData.photoIsRepresentative,
              reviewer: {
                name: '',
                imageUrl: '',
              },
            },
          };
        }
      );

      // Return context with previous data for rollback
      return { previousData };
    },
    onSuccess: () => {
      // Invalidate query to fetch fresh data from server
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.WORKER_ASSIGNMENT_JOB, jobAssignmentId],
      });

      // Call parent success callback
      onSuccess();
    },
    onError: (err: unknown, _variables, context) => {
      // Rollback to previous data on error
      if (context?.previousData) {
        queryClient.setQueryData(
          [QueryKeys.WORKER_ASSIGNMENT_JOB, jobAssignmentId],
          context.previousData
        );
      }

      // Show error message with type-safe error handling
      if (isAxiosError(err)) {
        const errorMessage =
          err.response?.data?.message || err.message || 'An error occurred';
        showWarningAlert(errorMessage);
      } else {
        console.error('Unexpected error:', err);
        showWarningAlert('An unexpected error occurred.');
      }
    },
  });

  const onSubmit: SubmitHandler<ReviewWorkerSchemaType> = (
    data: ReviewWorkerSchemaType
  ) => {
    submitRating(data);
  };

  if (totalWorkedDays) {
    setValue('approximatelyDaysWorked', totalWorkedDays);
  }

  return (
    <>
      {isPending && <ThLoading />}
      <Form
        className="review-worker"
        style={{ maxWidth: 'unset' }}
        onSubmit={handleSubmit(onSubmit)}
      >
        <fieldset
          disabled={isReadOnly}
          data-testid="review-worker-form-fieldset"
        >
          <Stack
            direction={isMobile ? 'vertical' : 'horizontal'}
            gap={isMobile ? 2 : 3}
          >
            <Stack direction="horizontal" gap={3}>
              <ShowImage
                className="avatar rounded cursor-point"
                src={worker?.profileImageUrl || defaultAvatar}
                style={{
                  width: 60,
                  height: 60,
                  objectFit: 'cover',
                }}
              />
              <span className="fw-bold">{worker?.fullName}</span>
            </Stack>
            <Controller
              control={control}
              name="performance"
              render={({ field }) => (
                <StarRatingButton
                  value={field.value}
                  setValue={(val) => field.onChange(Number(val))}
                  hideLabels
                  allowChange={!isReadOnly}
                  size={35}
                  gap={isMobile ? 2 : 3}
                />
              )}
            />
          </Stack>
          <hr />
          <Stack gap={3}>
            {ratingAssignmentData.isAnonReview && (
              <Badge bg="secondary" className="w-100 p-3 fw-bold fs-6">
                This review will appear as anonymous to the worker.
              </Badge>
            )}
            <Controller
              control={control}
              name="photoIsRepresentative"
              render={({ field }) => {
                return (
                  <Stack
                    direction={isMobile ? 'vertical' : 'horizontal'}
                    gap={3}
                    className="justify-content-between"
                  >
                    <Form.Label className={isMobile ? 'w-100' : 'w-50'}>
                      Does this picture look like them?*
                    </Form.Label>
                    <Stack>
                      <div className={isMobile ? 'w-100' : 'w-50'}>
                        <Form.Check
                          inline
                          type="radio"
                          label="Yes"
                          data-testid="check-yes"
                          checked={field.value}
                          onChange={() => field.onChange(true)}
                          isInvalid={!!errors.photoIsRepresentative}
                        />
                        <Form.Check
                          inline
                          type="radio"
                          label="No"
                          data-testid="check-no"
                          checked={field.value !== undefined && !field.value}
                          onChange={() => field.onChange(false)}
                          isInvalid={!!errors.photoIsRepresentative}
                        />
                      </div>

                      {errors.photoIsRepresentative && (
                        <span className="rate-error-message">
                          You must select one of these options
                        </span>
                      )}
                    </Stack>
                  </Stack>
                );
              }}
            />

            <Stack direction={isMobile ? 'vertical' : 'horizontal'} gap={3}>
              <Stack className={isMobile ? 'w-100' : 'w-50'} gap={2}>
                <Form.Label>Job Title*</Form.Label>
                <Form.Control
                  value={jobTitle?.name}
                  readOnly
                  className="text-capitalize"
                />
                <Form.Control
                  {...register('title.id', { valueAsNumber: true })}
                  type="hidden"
                  value={jobTitle?.id}
                />
              </Stack>
              <Stack className={isMobile ? 'w-100' : 'w-50'} gap={2}>
                <Form.Label>Approx. Days Worked*</Form.Label>
                <Form.Control
                  {...register('approximatelyDaysWorked', {
                    valueAsNumber: true,
                  })}
                  placeholder="Enter # of days"
                  className={
                    errors.approximatelyDaysWorked ? 'border-error' : ''
                  }
                  readOnly={!!totalWorkedDays}
                  type="number"
                />
              </Stack>
            </Stack>
            <Stack gap={2}>
              <Form.Label htmlFor="comments">
                What does this worker do well?{' '}
                <span className="optional-badge text-uppercase small">
                  (OPTIONAL)
                </span>
              </Form.Label>
              <Form.Control
                id="comments"
                {...register('comments')}
                as="textarea"
                maxLength={COMMENTS_MAX_LENGTH}
                onChange={(e) => setCommentsCount(e.target.value.length || 0)}
                autoComplete="off"
              />
              <Form.Text className="d-flex justify-content-end">
                {commentsCount}/{COMMENTS_MAX_LENGTH}
              </Form.Text>
            </Stack>
            <Stack gap={2}>
              <Form.Label>
                Did the worker do any of the following?{' '}
                <span className="optional-badge text-uppercase small">
                  (OPTIONAL)
                </span>
              </Form.Label>
              <Stack direction={isMobile ? 'vertical' : 'horizontal'} gap={3}>
                <Controller
                  control={control}
                  name="noCallNoShowed"
                  defaultValue={false}
                  render={({ field }) => {
                    const { value, ...rest } = field;
                    return (
                      <Button
                        variant={field.value === true ? 'primary' : 'light'}
                        className={`worker-behavior fw-bold text-uppercase ${
                          isMobile ? 'w-100' : 'w-50'
                        }`}
                        {...rest}
                        onClick={() => {
                          const newValue = !field.value;
                          field.onChange(newValue);
                          setValue('noCallNoShowed', newValue);
                        }}
                      >
                        No Call/No Show
                      </Button>
                    );
                  }}
                />

                <Controller
                  control={control}
                  name="canceledUnexcused"
                  render={({ field }) => {
                    const { value, ...rest } = field;

                    return (
                      <Button
                        variant={field.value === true ? 'primary' : 'light'}
                        className={`worker-behavior fw-bold text-uppercase ${
                          isMobile ? 'w-100' : 'w-50'
                        }`}
                        {...rest}
                        onClick={() => {
                          const newValue = !field.value;
                          field.onChange(newValue);
                          setValue('canceledUnexcused', newValue);
                        }}
                      >
                        Canceled within 24 hours
                      </Button>
                    );
                  }}
                />
              </Stack>
            </Stack>
            <Stack gap={2}>
              <Form.Label>Optional - What Can Worker Improve On</Form.Label>
              <p className="optional-badge small">
                Share improvements privately with the worker so they can
                improve, or have this appear publicly on the worker profile
                along with your name and company picture.
              </p>
            </Stack>
            <Stack gap={2}>
              <Controller
                control={control}
                name="visibility"
                render={({ field }) => {
                  return (
                    <div className="form-check d-flex align-items-center">
                      <input
                        type="checkbox"
                        className="form-check-input bigger"
                        checked={field.value === IRatingVisibility.PUBLIC}
                        onChange={(e) => {
                          const newValue = e.currentTarget.checked
                            ? IRatingVisibility.PUBLIC
                            : IRatingVisibility.PRIVATE;
                          field.onChange(newValue);
                        }}
                      />
                      {/* eslint-disable jsx-a11y/label-has-associated-control */}
                      <label className="form-check-label fw-bold ms-1 mt-1">
                        Agree to share this info publicly with your name?*
                      </label>
                    </div>
                  );
                }}
              />
            </Stack>
            <Stack gap={2}>
              <Form.Label>
                What can this worker improve?{' '}
                <span className="optional-badge text-uppercase small">
                  (OPTIONAL)
                </span>
              </Form.Label>
              <Form.Control
                {...register('improvementComments')}
                as="textarea"
                maxLength={COMMENTS_MAX_LENGTH}
                onChange={(e) =>
                  setImprovementCommentsCount(e.target.value.length || 0)
                }
                autoComplete="off"
              />
              <Form.Text className="d-flex justify-content-end">
                {improvementCommentsCount}/{COMMENTS_MAX_LENGTH}
              </Form.Text>
            </Stack>
            <Stack>
              <hr className="table-horizontal-line" />
            </Stack>
            {isPremiumCompany && (
              <Stack gap={2}>
                <Form.Label>
                  Internal Note on Worker Profile{' '}
                  <span className="optional-badge text-uppercase small">
                    (OPTIONAL, ONLY YOUR COMPANY CAN SEE THESE)
                  </span>
                </Form.Label>
                <Form.Control
                  {...register('internalNotes')}
                  as="textarea"
                  maxLength={COMMENTS_MAX_LENGTH}
                  onChange={(e) =>
                    setInternalNotesCount(e.target.value.length || 0)
                  }
                  autoComplete="off"
                />
                <Form.Text className="d-flex justify-content-end">
                  {internalNotesCount}/{COMMENTS_MAX_LENGTH}
                </Form.Text>
              </Stack>
            )}

            {!isReadOnly && (
              <div className="d-flex justify-content-end">
                <Button type="submit" disabled={!isValid}>
                  Submit Review
                </Button>
              </div>
            )}

            {isReadOnly && workerRating?.ratingReply && (
              <Stack gap={2}>
                <hr className="table-horizontal-line" />
                <Form.Label className="fw-bold">Worker Reply</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={workerRating.ratingReply.comments}
                  readOnly
                  className="bg-light"
                />
              </Stack>
            )}
          </Stack>
          <Form.Control
            {...register('jobAssignmentId', { valueAsNumber: true })}
            type="hidden"
            readOnly
            value={jobAssignmentId}
          />
          <Form.Control
            {...register('workerId', { valueAsNumber: true })}
            type="hidden"
            readOnly
            value={worker?.id}
          />
          <Form.Control
            {...register('jobId')}
            type="hidden"
            readOnly
            value={jobId ?? ''}
          />
        </fieldset>
      </Form>
      <Alert
        show={alertMessage.show}
        variant={alertMessage.variant}
        className="alert-fixed"
        style={{ width: '20rem' }}
      >
        <Alert.Heading>{alertMessage.message}</Alert.Heading>
      </Alert>
    </>
  );
}

export default ComHubReviewWorkerForm;
